import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  ExpenseStatusBadge,
  useToast,
  FilterBar,
  SavedFiltersMenu,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { expenseService } from '@/services/expense.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useListFilterBar } from '@/features/saved-filters';
import { downloadFile } from '@/lib/download-file';
import { ROLE_LEVELS } from '@/types/user';
import { CATEGORY_LABELS, CATEGORY_OPTIONS } from '@/types/expense';
import type { Expense } from '@/types/expense';

type ExpenseRow = Expense & { id: string };

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'HOLD', label: 'On Hold' },
];

// T27: the shared filter bar. Keys are the list endpoint's own parameters.
// Expenses carry no customer or mobile: the facets are the description and the
// bill / reference number.
export const EXPENSE_FILTER_KEYS = ['description', 'reference', 'status', 'category', 'start_date', 'end_date'];
const EXPENSE_FACETS: FilterBarFacet[] = [
  { key: 'description', label: 'Description' },
  { key: 'reference', label: 'Reference no.', kind: 'number' },
];
// expense_date is a calendar day already — no zone conversion on either side.
const EXPENSE_DATE = { fromKey: 'start_date', toKey: 'end_date', label: 'Expense date' };

// ─── KPI Card (local, matches InventoryDashboard pattern) ────────────────────

interface KpiConfig {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
}

function KpiCard({ title, value, icon, gradient, iconBg, tintBg, darkTintBg }: KpiConfig) {
  return (
    <div className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 animate-slide-up`}>
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20`} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function ReceiptIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function CurrencyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PauseCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PaperClipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );
}

// ─── ExpenseListContent ──────────────────────────────────────────────────────

function ExpenseListContent() {
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  const [isExporting, setIsExporting] = useState(false);

  // Presets are the header store's days (T18), else the business's.
  const bar = useListFilterBar({
    page: 'expenses',
    keys: EXPENSE_FILTER_KEYS,
    date: EXPENSE_DATE,
    locationId: selectedLocationId,
  });
  const { values } = bar;
  const page = Number(searchParams.get('page') ?? '1');
  const setPage = (p: number) => bar.onChange({ page: String(p) });

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isCashier = roleLevel <= ROLE_LEVELS.CASHIER;

  const listParams = {
    status: values.status,
    category: values.category,
    description: values.description,
    reference: values.reference,
    location_id: selectedLocationId ?? undefined,
    from: values.start_date,
    to: values.end_date,
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ['expenses', { page, ...listParams }],
    queryFn: () => expenseService.list({ ...listParams, page, limit: 20 }),
    staleTime: 15_000,
  });

  // Every matching expense, all pages — the API writes the file (T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await expenseService.exportCsv(listParams), 'expenses.csv');
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error]);

  // Keyboard shortcut: n → new expense
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (
        e.key === 'n' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        navigate('/expenses/new');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const expenses = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const summary = data?.summary;

  // Fallback stats from current page if server summary not available
  const totalAmount = summary?.total_amount ?? expenses.reduce((s, e) => s + e.amount, 0);
  const pendingCount = summary?.pending_count ?? expenses.filter((e) => e.status === 'PENDING').length;
  const approvedCount = summary?.approved_count ?? expenses.filter((e) => e.status === 'APPROVED').length;
  const rejectedCount = summary?.rejected_count ?? expenses.filter((e) => e.status === 'REJECTED').length;
  const holdCount = summary?.hold_count ?? expenses.filter((e) => e.status === 'HOLD').length;

  const columns: ColumnDef<ExpenseRow>[] = [
    {
      key: 'expense_date',
      header: 'Date',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/expenses/${row.id}`)}
          className="text-sm text-brand-600 dark:text-brand-400 hover:underline tabular-nums text-left"
        >
          {new Date(row.expense_date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </button>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {CATEGORY_LABELS[row.category]}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 truncate max-w-[200px] block">
          {row.description.length > 60 ? row.description.slice(0, 60) + '\u2026' : row.description}
        </span>
      ),
    },
    ...(!isCashier
      ? ([
          {
            key: 'submitted_by_name',
            header: 'Submitted By',
            render: (_v: unknown, row: ExpenseRow) => (
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {row.submitted_by_name}
              </span>
            ),
          },
        ] as ColumnDef<ExpenseRow>[])
      : []),
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {formatCurrency(row.amount, currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <ExpenseStatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'receipt_url',
      header: 'Receipt',
      render: (_v, row) =>
        row.receipt_url ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400" title="Receipt attached">
            <PaperClipIcon className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span className="text-neutral-300 dark:text-neutral-600">&mdash;</span>
        ),
    },
  ];

  // ── Loading skeleton ─────────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-xl" />
      </>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────────

  if (!isLoading && total === 0 && Object.keys(values).length === 0) {
    return (
      <>
        {/* Gradient header */}
        <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Expenses</h1>
          <p className="text-warning-100 mt-1">Track and manage all business expenses</p>
        </div>

        <EmptyState
          variant="page"
          icon={<ReceiptIcon className="h-full w-full" />}
          title="No expenses recorded"
          description="Add your first expense to start tracking business spending and generate P&L reports"
          action={{ label: 'Add Expense', onClick: () => navigate('/expenses/new') }}
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Expenses</h1>
            <p className="text-warning-100 mt-1">Track and manage all business expenses</p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/expenses/new')} className="bg-white/20 hover:bg-white/30 text-white border-white/30">
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-6">
        <KpiCard
          title="Total Amount"
          value={formatCurrency(totalAmount, currency)}
          gradient="bg-gradient-to-br from-warning-500 to-warning-700"
          iconBg="bg-gradient-to-br from-warning-500 to-warning-600"
          tintBg="bg-warning-50"
          darkTintBg="dark:bg-warning-900/20"
          icon={<CurrencyIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Pending"
          value={pendingCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-amber-500 to-amber-700"
          iconBg="bg-gradient-to-br from-amber-500 to-amber-600"
          tintBg="bg-amber-50"
          darkTintBg="dark:bg-amber-900/20"
          icon={<ClockIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Approved"
          value={approvedCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-success-500 to-success-700"
          iconBg="bg-gradient-to-br from-success-500 to-success-600"
          tintBg="bg-success-50"
          darkTintBg="dark:bg-success-900/20"
          icon={<CheckCircleIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Rejected"
          value={rejectedCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-danger-500 to-danger-700"
          iconBg="bg-gradient-to-br from-danger-500 to-danger-600"
          tintBg="bg-danger-50"
          darkTintBg="dark:bg-danger-900/20"
          icon={<XCircleIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="On Hold"
          value={holdCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-violet-500 to-violet-700"
          iconBg="bg-gradient-to-br from-violet-500 to-violet-600"
          tintBg="bg-violet-50"
          darkTintBg="dark:bg-violet-900/20"
          icon={<PauseCircleIcon className="h-5 w-5" />}
        />
      </div>

      {/* Filters (T27) — the KPI cards above are the summary: every matching expense, not the page */}
      <div className="mb-4">
        <FilterBar
          facets={EXPENSE_FACETS}
          selects={[
            { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
            { key: 'category', label: 'Category', options: CATEGORY_OPTIONS, allLabel: 'All categories' },
          ]}
          date={EXPENSE_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          placeholder="Type a description or reference number…"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
          actions={
            <Button variant="secondary" onClick={handleExport} disabled={total === 0 || isExporting}>
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          }
        />
      </div>

      {/* Data Table */}
      <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md animate-slide-up">
        <DataTable<ExpenseRow>
          columns={columns}
          data={expenses.map((e) => ({ ...e, id: e.id }))}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No expenses match filters',
            description: 'Try adjusting your filters or date range',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export with ErrorBoundary ──────────────────────────────────────────

export function ExpenseListPage() {
  return (
    <PageContainer
      title="Expenses"
      breadcrumbs={[{ label: 'Expenses' }]}
    >
      <ErrorBoundary>
        <ExpenseListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
